// Formularios de escritura del inventario.
//
// Cada uno refleja una regla del backend en la interfaz, para no ofrecer
// botones que van a fallar:
//
// - Un insumo que lleva caducidad exige fecha en la entrada.
// - La merma exige causa del catálogo Y foto.
// - Un traslado no puede tener el mismo origen y destino.
//
// El candado real sigue siendo del servidor; esto solo evita el viaje.

import { useEffect, useState } from 'react';
import { Loader2, Plus, X } from 'lucide-react';

import { inventoryApi, InventoryApiError } from './api';
import type { Supply, WasteReason } from './types';
import type { Location } from '@/modules/pos';

const inputClass =
  'mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground ' +
  'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40';

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="block text-xs text-muted-foreground mt-1">{hint}</span>}
    </label>
  );
}

function Submit({ children, busy, disabled }: { children: React.ReactNode; busy?: boolean; disabled?: boolean }) {
  return (
    <button
      type="submit"
      disabled={busy || disabled}
      className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium
                 text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {busy && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}

function FormError({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <p className="text-xs text-red-500 mt-2 rounded-lg bg-red-500/10 border border-red-500/30 px-2.5 py-2">
      {error}
    </p>
  );
}

function Panel({
  label,
  children,
}: {
  label: string;
  children: (close: () => void) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5
                   text-xs text-muted-foreground hover:text-foreground"
      >
        <Plus className="w-3.5 h-3.5" />
        {label}
      </button>
    );
  }
  return (
    <div className="w-full rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <button type="button" onClick={() => setOpen(false)} aria-label="Cerrar">
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
      {children(() => setOpen(false))}
    </div>
  );
}

const slugify = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/** Catálogo de insumos, compartido por los formularios que lo necesitan. */
function useSupplies(open: boolean) {
  const [supplies, setSupplies] = useState<Supply[]>([]);
  useEffect(() => {
    if (!open) return;
    inventoryApi.supplies().then(setSupplies).catch(() => undefined);
  }, [open]);
  return supplies;
}

// ── Alta de insumo ──────────────────────────────────────────────

export function NewSupplyForm({ onDone }: { onDone: () => void }) {
  return (
    <Panel label="Nuevo insumo">
      {(close) => <NewSupplyFields onDone={() => { close(); onDone(); }} />}
    </Panel>
  );
}

function NewSupplyFields({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('kilo');
  const [caduca, setCaduca] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await inventoryApi.createSupply({
        name: name.trim(),
        slug: slugify(name),
        unit,
        tracks_expiry: caduca,
      });
      onDone();
    } catch (err) {
      setError(err instanceof InventoryApiError ? err.message : 'No se pudo crear');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-[1fr_140px_auto] sm:items-end">
      <Field label="Nombre">
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Carne de cerdo" required />
      </Field>
      <Field label="Unidad">
        <select className={inputClass} value={unit} onChange={(e) => setUnit(e.target.value)}>
          {['kilo', 'litro', 'pieza', 'caja', 'bolsa', 'tanque'].map((u) => (
            <option key={u} value={u}>{u}</option>
          ))}
        </select>
      </Field>
      <Submit busy={busy} disabled={!name.trim()}>Crear</Submit>
      <label className="sm:col-span-3 flex items-center gap-2 text-sm text-muted-foreground">
        <input type="checkbox" checked={caduca} onChange={(e) => setCaduca(e.target.checked)} />
        Lleva control de caducidad
        <span className="text-xs">(desactívalo para gas, desechables y cosas que no caducan)</span>
      </label>
      <div className="sm:col-span-3"><FormError error={error} /></div>
    </form>
  );
}

// ── Entrada ─────────────────────────────────────────────────────

export function EntryForm({ locations, onDone }: { locations: Location[]; onDone: () => void }) {
  return (
    <Panel label="Registrar entrada">
      {(close) => <EntryFields locations={locations} onDone={() => { close(); onDone(); }} />}
    </Panel>
  );
}

function EntryFields({ locations, onDone }: { locations: Location[]; onDone: () => void }) {
  const supplies = useSupplies(true);
  const [supplyId, setSupplyId] = useState('');
  const [locationId, setLocationId] = useState(locations[0]?.id || '');
  const [cantidad, setCantidad] = useState('');
  const [caducidad, setCaducidad] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const elegido = supplies.find((s) => s.id === supplyId);
  const exigeFecha = !!elegido?.tracks_expiry;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await inventoryApi.entry({
        supply_id: supplyId,
        location_id: locationId,
        quantity: cantidad.trim(),
        expires_on: exigeFecha ? caducidad : null,
      });
      onDone();
    } catch (err) {
      setError(err instanceof InventoryApiError ? err.message : 'No se pudo registrar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
      <Field label="Insumo">
        <select className={inputClass} value={supplyId} onChange={(e) => setSupplyId(e.target.value)} required>
          <option value="">Elige…</option>
          {supplies.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.unit})</option>)}
        </select>
      </Field>
      <Field label="Punto">
        <select className={inputClass} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </Field>
      <Field label={`Cantidad${elegido ? ` (${elegido.unit})` : ''}`}>
        <input className={inputClass} value={cantidad} onChange={(e) => setCantidad(e.target.value)} inputMode="decimal" placeholder="0" required />
      </Field>
      {exigeFecha && (
        <Field label="Caduca el" hint="Este insumo lleva control de caducidad.">
          <input type="date" className={inputClass} value={caducidad} onChange={(e) => setCaducidad(e.target.value)} required />
        </Field>
      )}
      <div className="sm:col-span-2 flex items-center gap-3">
        <Submit busy={busy} disabled={!supplyId || !cantidad.trim() || (exigeFecha && !caducidad)}>
          Registrar entrada
        </Submit>
        <p className="text-xs text-muted-foreground">Cada entrada crea un lote propio; no se mezcla con los anteriores.</p>
      </div>
      <div className="sm:col-span-2"><FormError error={error} /></div>
    </form>
  );
}

// ── Salida: merma / consumo / ajuste ────────────────────────────

export function ExitForm({ locations, onDone }: { locations: Location[]; onDone: () => void }) {
  return (
    <Panel label="Merma o salida">
      {(close) => <ExitFields locations={locations} onDone={() => { close(); onDone(); }} />}
    </Panel>
  );
}

function ExitFields({ locations, onDone }: { locations: Location[]; onDone: () => void }) {
  const supplies = useSupplies(true);
  const [reasons, setReasons] = useState<WasteReason[]>([]);
  const [kind, setKind] = useState('waste');
  const [supplyId, setSupplyId] = useState('');
  const [locationId, setLocationId] = useState(locations[0]?.id || '');
  const [cantidad, setCantidad] = useState('');
  const [reasonId, setReasonId] = useState('');
  const [foto, setFoto] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    inventoryApi.wasteReasons().then(setReasons).catch(() => undefined);
  }, []);

  const esMerma = kind === 'waste';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await inventoryApi.exit({
        supply_id: supplyId,
        location_id: locationId,
        quantity: cantidad.trim(),
        kind,
        waste_reason_id: esMerma ? reasonId : null,
        photo_url: esMerma ? foto.trim() : '',
      });
      onDone();
    } catch (err) {
      setError(err instanceof InventoryApiError ? err.message : 'No se pudo registrar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
      <Field label="Tipo">
        <select className={inputClass} value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="waste">Merma</option>
          <option value="consumption">Consumo</option>
          <option value="adjustment">Ajuste por conteo</option>
        </select>
      </Field>
      <Field label="Punto">
        <select className={inputClass} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </Field>
      <Field label="Insumo">
        <select className={inputClass} value={supplyId} onChange={(e) => setSupplyId(e.target.value)} required>
          <option value="">Elige…</option>
          {supplies.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.unit})</option>)}
        </select>
      </Field>
      <Field label="Cantidad">
        <input className={inputClass} value={cantidad} onChange={(e) => setCantidad(e.target.value)} inputMode="decimal" placeholder="0" required />
      </Field>

      {esMerma && (
        <>
          <Field label="Causa" hint="Obligatoria. Sin causa no hay merma.">
            <select className={inputClass} value={reasonId} onChange={(e) => setReasonId(e.target.value)} required>
              <option value="">Elige…</option>
              {reasons.map((r) => <option key={r.id} value={r.id}>{r.text}</option>)}
            </select>
          </Field>
          <Field label="Foto (URL)" hint="Obligatoria. Es la evidencia de lo que se tiró.">
            <input className={inputClass} value={foto} onChange={(e) => setFoto(e.target.value)} placeholder="https://…" required />
          </Field>
        </>
      )}

      <div className="sm:col-span-2 flex items-center gap-3">
        <Submit
          busy={busy}
          disabled={!supplyId || !cantidad.trim() || (esMerma && (!reasonId || !foto.trim()))}
        >
          Registrar
        </Submit>
        <p className="text-xs text-muted-foreground">Descuenta del lote que caduca primero.</p>
      </div>
      <div className="sm:col-span-2"><FormError error={error} /></div>
    </form>
  );
}

// ── Traslado ────────────────────────────────────────────────────

export function TransferForm({ locations, onDone }: { locations: Location[]; onDone: () => void }) {
  return (
    <Panel label="Nuevo traslado">
      {(close) => <TransferFields locations={locations} onDone={() => { close(); onDone(); }} />}
    </Panel>
  );
}

function TransferFields({ locations, onDone }: { locations: Location[]; onDone: () => void }) {
  const supplies = useSupplies(true);
  const [supplyId, setSupplyId] = useState('');
  const [origen, setOrigen] = useState(locations.find((l) => l.kind === 'warehouse')?.id || locations[0]?.id || '');
  const [destino, setDestino] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mismoPunto = !!origen && origen === destino;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await inventoryApi.transfer({
        supply_id: supplyId,
        origin_id: origen,
        destination_id: destino,
        quantity: cantidad.trim(),
      });
      onDone();
    } catch (err) {
      setError(err instanceof InventoryApiError ? err.message : 'No se pudo iniciar el traslado');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
      <Field label="Insumo">
        <select className={inputClass} value={supplyId} onChange={(e) => setSupplyId(e.target.value)} required>
          <option value="">Elige…</option>
          {supplies.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.unit})</option>)}
        </select>
      </Field>
      <Field label="Cantidad">
        <input className={inputClass} value={cantidad} onChange={(e) => setCantidad(e.target.value)} inputMode="decimal" placeholder="0" required />
      </Field>
      <Field label="Sale de">
        <select className={inputClass} value={origen} onChange={(e) => setOrigen(e.target.value)}>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </Field>
      <Field label="Llega a">
        <select className={inputClass} value={destino} onChange={(e) => setDestino(e.target.value)} required>
          <option value="">Elige…</option>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </Field>
      {mismoPunto && (
        <p className="sm:col-span-2 text-xs text-amber-500">
          El origen y el destino no pueden ser el mismo punto.
        </p>
      )}
      <div className="sm:col-span-2 flex items-center gap-3">
        <Submit busy={busy} disabled={!supplyId || !cantidad.trim() || !destino || mismoPunto}>
          Autorizar salida
        </Submit>
        <p className="text-xs text-muted-foreground">
          Descuenta del origen y queda en tránsito hasta que alguien más confirme la recepción.
        </p>
      </div>
      <div className="sm:col-span-2"><FormError error={error} /></div>
    </form>
  );
}
